import React from "react";
import { CardHeader, CardBody, Row, Col, FormGroup, Label, Input, Button, Alert } from "reactstrap";
import { withTranslation } from "react-i18next";
import withRouter from "../../../../../components/Common/withRouter";

const BonusForm = ({
  allBonuses,
  selectedBonus,
  setSelectedBonus,
  customAmount,
  setCustomAmount,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isTaxable,
  setIsTaxable,
  handleSave,
  currentBonus,
  isSaving,
  backendError,
  isBonusAlreadyAssigned,
  t
}) => {

  const selectedBonusObj = allBonuses.find(b => b.id === selectedBonus);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Check if dates are valid
  const isStartDateValid = startDate && startDate >= getTodayDate();
  const isEndDateValid = !endDate || (endDate && startDate && endDate >= startDate);
  const areDatesValid = isStartDateValid && isEndDateValid;

  const canSave =
    currentBonus &&
    areDatesValid &&
    !isSaving &&
    customAmount &&
    parseFloat(customAmount) > 0;

  // Handle bonus selection
  const handleBonusChange = (e) => {
    const bonusId = e.target.value;
    if (bonusId && isBonusAlreadyAssigned && isBonusAlreadyAssigned(bonusId)) {
      return;
    }
    setSelectedBonus(bonusId);
  };

  // Handle amount change
  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 0)) {
      setCustomAmount(value);
    }
  };

  return (
    <div className="card border-0 row p-2 mt-4">
      <CardHeader className="bg-white border-0 pb-0">
        <h6 className="mb-0">
          <i className="fa fa-gift me-2 text-success"></i>
          {t("assign_bonuses")}
        </h6>
      </CardHeader>
      <CardBody>
        {/* Alerte si aucun bonus disponible */}
        {/* {allBonuses.length === 0 && (
          <Alert color="warning" className="mb-4">
            <i className="fa fa-exclamation-triangle me-2"></i>
            {t("no_bonuses_available")}
          </Alert>
        )} */}

        {/* Alerte d'erreur backend */}
        {/* {backendError && (
          <Alert color="danger" className="mb-4">
            <i className="fa fa-exclamation-circle me-2"></i>
            {backendError}
          </Alert>
        )} */}

        {/* Formulaire - seulement si des bonus sont disponibles */}
        {allBonuses.length > 0 && (
          <>
            <Row className="mb-4">
              {/* Select bonus */}
              <Col xl={4} lg={6} md={12} className="mb-2">
                <FormGroup>
                  <Label className="form-label fw-semibold">
                    {t("select_bonus")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={selectedBonus}
                    onChange={handleBonusChange}
                    className="form-select custom-border"
                    disabled={isSaving}
                    required
                  >
                    <option value="">{t("select_bonus")}...</option>
                    {allBonuses.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Custom amount */}
              {selectedBonus && (
                <Col xl={3} lg={4} md={6} className="mb-2">
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      {t("Amount")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={customAmount}
                      onChange={handleAmountChange}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      className="custom-border"
                      disabled={isSaving}
                      required
                      matches="^[0-9]+(\.[0-9]{1,2})?$"
                    />
                  </FormGroup>
                </Col>
              )}

              {/* Start date */}
              {selectedBonus && (
                <Col xl={2} lg={3} md={6} className="mb-2">
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      {t("start_date")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={getTodayDate()}
                      className={`custom-border ${startDate && !isStartDateValid ? 'is-invalid' : ''}`}
                      required
                      disabled={isSaving}
                    />
                    {startDate && !isStartDateValid && (
                      <div className="invalid-feedback d-block">
                        {t("start_date_must_be_equal_or_greater_than_today")}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              )}

              {/* End date (optionnelle) */}
              {selectedBonus && (
                <Col xl={2} lg={3} md={6} className="mb-2">
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      {t("end_date")}
                    </Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || getTodayDate()}
                      className={`custom-border ${endDate && !isEndDateValid ? 'is-invalid' : ''}`}
                      disabled={!startDate || isSaving}
                    />
                    {endDate && !isEndDateValid && (
                      <div className="invalid-feedback d-block">
                        {t("end_date_must_be_equal_or_greater_than_start_date")}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              )}

              {/* Taxable checkbox */}
              {selectedBonus && (
                <Col xl={1} lg={2} md={6} className="mb-2 d-flex align-items-end">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      checked={isTaxable}
                      onChange={(e) => setIsTaxable(e.target.checked)}
                      disabled={isSaving}
                      id="taxableCheck"
                    />
                    <Label check for="taxableCheck" className="form-label fw-semibold">
                      {t("Taxable")}
                    </Label>
                  </FormGroup>
                </Col>
              )}
            </Row>

            {/* Save Button */}
            <Row className="mb-4">
              <Col xl={3} lg={4} md={6} className="mb-2">
                <Button
                  color="success"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="w-100"
                  size="md"
                >
                  {isSaving ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      {t("recording")}...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check me-2"></i>
                      {t("Save")}
                    </>
                  )}
                </Button>
                {/* {!canSave && !isSaving && (
                  <small className="form-text text-muted mt-1">
                    {selectedBonus && !customAmount && t("Enter an amount")}
                    {selectedBonus && customAmount && parseFloat(customAmount) <= 0 && t("Amount must be greater than 0")}
                    {selectedBonus && customAmount && parseFloat(customAmount) > 0 && !startDate && t("Enter a start date")}
                    {selectedBonus && customAmount && parseFloat(customAmount) > 0 && startDate && !isStartDateValid && t("Check start date")}
                    {selectedBonus && customAmount && parseFloat(customAmount) > 0 && startDate && isStartDateValid && endDate && !isEndDateValid && t("Check end date")}
                  </small>
                )} */}
              </Col>
            </Row>
          </>
        )}
      </CardBody>
    </div>
  );
};

export default withRouter(withTranslation()(BonusForm));